import { DataProvider } from "@refinedev/core";
import {
  mockReviews,
  mockResponses,
  mockStrategies,
  mockStopWords,
  mockTriggerWords,
  mockUsers,
  templates,
  mockKnowledgeBase
} from "../../mocks/data";

// In-memory storage
const storage: Record<string, any[]> = {
  reviews: [...mockReviews],
  responses: [...mockResponses],
  strategies: [...mockStrategies],
  "stop-words": [...mockStopWords],
  "trigger-words": [...mockTriggerWords],
  users: [...mockUsers],
  templates: [...templates],
  "knowledge-base": [...mockKnowledgeBase],
};

// Helper: filter data based on filters
function applyFilters(data: any[], filters: any[]): any[] {
  if (!filters || filters.length === 0) return data;

  return data.filter((item) => {
    return filters.every((filter: any) => {
      const { field, operator, value } = filter;
      const itemValue = item[field];

      if (value == null) return true;

      switch (operator) {
        case "eq":
        case "equals":
          return itemValue === value;
        case "ne":
          return itemValue !== value;
        case "null":
          return itemValue == null || itemValue === null || itemValue === undefined;
        case "nnull":
          return itemValue != null && itemValue !== null && itemValue !== undefined;
        case "lt":
          return itemValue < value;
        case "lte":
        case "<=":
          return itemValue <= value;
        case "gt":
          return itemValue > value;
        case "gte":
        case ">=":
          return itemValue >= value;
        case "contains":
          return String(itemValue).toLowerCase().includes(String(value).toLowerCase());
        case "in":
          return Array.isArray(value) && value.includes(itemValue);
        case "is":
          return itemValue === value;
        default:
          // Default: try string contains for text fields
          if (typeof itemValue === "string") {
            return itemValue.toLowerCase().includes(String(value).toLowerCase());
          }
          return itemValue === value;
      }
    });
  });
}

// Helper: apply sorting
function applySorters(data: any[], sorters?: any[]): any[] {
  if (!sorters || sorters.length === 0) return data;

  const sortedData = [...data];
  sorters.forEach((sorter: any) => {
    const { field, order } = sorter;
    sortedData.sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
  });

  return sortedData;
}

// Helper: apply pagination
function applyPagination(data: any[], pagination?: any): any[] {
  if (!pagination) return data;

  const { current = 1, pageSize = 20 } = pagination;
  const start = (current - 1) * pageSize;
  const end = start + pageSize;

  return data.slice(start, end);
}

export const mockDataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    // Normalize resource name
    const resourceKey = resource;
    let data = storage[resourceKey] || [];

    // Apply filters
    if (filters) {
      data = applyFilters(data, filters as any[]);
    }

    // Apply sorting
    if (sorters) {
      data = applySorters(data, sorters);
    }

    const total = data.length;

    // Apply pagination
    if (pagination) {
      data = applyPagination(data, pagination);
    }

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 100));

    return { data, total };
  },

  getOne: async ({ resource, id }) => {
    const resourceKey = resource;
    const data = storage[resourceKey] || [];
    const item = data.find((d) => d.id === id);

    if (!item) {
      throw new Error(`Item with id ${id} not found in ${resource}`);
    }

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 50));

    return { data: item };
  },

  create: async ({ resource, variables }) => {
    const resourceKey = resource;
    if (!storage[resourceKey]) {
      storage[resourceKey] = [];
    }

    // Generate new ID
    const newId = `${resourceKey.slice(0, 3)}-${Date.now()}`;
    const newItem = {
      id: newId,
      ...variables,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    storage[resourceKey].push(newItem);

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 100));

    return { data: newItem };
  },

  update: async ({ resource, id, variables }) => {
    const resourceKey = resource;
    const data = storage[resourceKey] || [];
    const index = data.findIndex((d) => d.id === id);

    if (index === -1) {
      throw new Error(`Item with id ${id} not found in ${resource}`);
    }

    const updatedItem = {
      ...data[index],
      ...variables,
      updated_at: new Date().toISOString(),
    };

    storage[resourceKey][index] = updatedItem;

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 100));

    return { data: updatedItem };
  },

  deleteOne: async ({ resource, id }) => {
    const resourceKey = resource;
    const data = storage[resourceKey] || [];
    const index = data.findIndex((d) => d.id === id);

    if (index === -1) {
      throw new Error(`Item with id ${id} not found in ${resource}`);
    }

    const deletedItem = data[index];
    storage[resourceKey].splice(index, 1);

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 100));

    return { data: deletedItem };
  },

  getApiUrl: () => "mock://localhost",
} as any;
